import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import React, { Suspense } from 'react';
import { Role } from './common/enums';
import useAuthStore from './datastore/useAuth';
import LoadingSpinner from './components/loadingSpinner/LoadingSpinner';

const AccountsPage = React.lazy(() => import('./pages/Admin/accounts/AccountsPage'));
const ComponentArchive = React.lazy(() => import('./pages/Admin/componentManagement/archive/ComponentArchive'));
const ComponentsPage = React.lazy(() => import('./pages/Admin/componentManagement/ComponentsPage'));
const ComponentTypeArchive = React.lazy(() => import('./pages/Admin/componentTypes/archive/ComponentTypeArchive'));
const ComponentTypesPage = React.lazy(() => import('./pages/Admin/componentTypes/ComponentTypesPage'));
const CustomerManagementPage = React.lazy(() => import('./pages/staff/customers/CustomerManagementPage'));
const PopularProductsPage = React.lazy(() => import('./pages/Admin/popular/PopularProductsPage'));
const ProductsPage = React.lazy(() => import('./pages/Admin/products/ProductPage'));
const VariationPage = React.lazy(() => import('./pages/Admin/products/variations/VariationPage'));
const ReportsPage = React.lazy(() => import('./pages/Admin/reports/ReportsPage'));
const SuppliersPage = React.lazy(() => import('./pages/Admin/suppliers/SuppliersPage'));
const CustomerCheckoutPage = React.lazy(() => import('./pages/customer/checkout/CustomerCheckoutPage'));
const CustomerHomePage = React.lazy(() => import('./pages/customer/home/CustomerHomePage'));
import CustomerLayout from './pages/customer/layout/CustomerLayout';

const OrderConfirmed = React.lazy(() => import('./pages/customer/orderConfirmed/OrderConfirmed'));
const OrderHistoryPage = React.lazy(() => import('./pages/customer/orderHistory/OrderHistoryPage'));
const CustomerProfilePage = React.lazy(() => import('./pages/customer/profile/CutomerProfilePage'));
const PasswordResetPage = React.lazy(() => import('./pages/passwordReset/PasswordResetPage'));
const StaffHomePage = React.lazy(() => import('./pages/staff/home/StaffHomePage'));
import StaffLayout from './pages/staff/layout/StaffLayout';

const OrderPage = React.lazy(() => import('./pages/staff/order/OrderPage'));
const OrderDetailsPage = React.lazy(() => import('./pages/staff/pos/OrderDetailsPage'));
const PosPage = React.lazy(() => import('./pages/staff/pos/PosPage'));
const StaffSettings = React.lazy(() => import('./pages/staff/settings/StaffSettings'));
const WalkUpPage = React.lazy(() => import('./pages/staff/walkup/WalkUpPage'));
const EodPage = React.lazy(() => import('./pages/staff/eod/EodPage'));
const EodReportsPage = React.lazy(() => import('./pages/Admin/eod/EodReportsPage'));
import CustomerSuspended from './components/customerSuspended/CustomerSuspended';

const SitesPage = React.lazy(() => import('./pages/Admin/Sites/SitesPage'));
const PurchaseOrdersPage = React.lazy(() => import('./pages/Admin/inventory/PurchaseOrdersPage'));
const InventoryUsagePage = React.lazy(() => import('./pages/Admin/inventory/InventoryUsagePage'));
const StaffInventoryPage = React.lazy(() => import('./pages/staff/inventory/StaffInventoryPage'));
const ReceivingPage = React.lazy(() => import('./pages/staff/inventory/ReceivingPage'));
const TransfersPage = React.lazy(() => import('./pages/staff/inventory/TransfersPage'));
const AdminTransfersPage = React.lazy(() => import('./pages/Admin/inventory/AdminTransfersPage'));

/**
 * Generic role-based route guard.
 * Renders child routes only if the current user's role is in the allowedRoles list.
 */
const RoleGuard = ({ allowedRoles }: { allowedRoles: Role[] }) => {
    const role = useAuthStore(state => state.role);
    const currentUser = useAuthStore(state => state.currentUser);

    if (!currentUser || !role || !allowedRoles.includes(role)) {
        return <Navigate to="/" replace />;
    }
    return <Outlet />;
};

const Suspended = ({ children }: { children: React.ReactNode }) => (
    <Suspense fallback={<LoadingSpinner isLoading={true} />}>
        {children}
    </Suspense>
);

const publicRoutes = [
    {
        path: '',
        element: (
            <CustomerLayout>
                <Suspended>
                    <CustomerHomePage />
                </Suspended>
            </CustomerLayout>
        ),
    },
    {
        path: 'suspended',
        element: (
            <CustomerLayout>
                <Suspended>
                    <CustomerSuspended />
                </Suspended>
            </CustomerLayout>
        )
    },
    {
        path: 'checkout',
        element: (
            <CustomerLayout>
                <Suspended>
                    <CustomerCheckoutPage />
                </Suspended>
            </CustomerLayout>
        ),
    },
    {
        path: 'forgot_password',
        element: (
            <CustomerLayout>
                <Suspended>
                    <PasswordResetPage mode="forgot" />
                </Suspended>
            </CustomerLayout>
        ),
    },
    {
        path: 'reset_password',
        element: (
            <CustomerLayout>
                <Suspended>
                    <PasswordResetPage mode="reset" />
                </Suspended>
            </CustomerLayout>
        ),
    },
]

const customerRoutes = [
    {
        path: 'profile',
        element: (
            <CustomerLayout>
                <Suspended>
                    <CustomerProfilePage />
                </Suspended>
            </CustomerLayout>
        ),
    },
    {
        path: 'orders',
        element: (
            <CustomerLayout>
                <Suspended>
                    <OrderHistoryPage />
                </Suspended>
            </CustomerLayout>
        ),
    },
    {
        path: 'orderconfirmed/:orderId',
        element: (
            <CustomerLayout>
                <Suspended>
                    <OrderConfirmed />
                </Suspended>
            </CustomerLayout>
        ),
    },
]

/** Accessible to all staff roles: Admin, SiteAdmin, General */
const staffRoutes = [
    {
        path: 'home',
        element: (
            <StaffLayout>
                <Suspended>
                    <StaffHomePage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'order',
        element: (
            <StaffLayout>
                <Suspended>
                    <OrderPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'pos',
        element: (
            <StaffLayout>
                <Suspended>
                    <PosPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'pos/:orderId',
        element: (
            <StaffLayout>
                <Suspended>
                    <OrderDetailsPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'settings/:siteId?',
        element: (
            <StaffLayout>
                <Suspended>
                    <StaffSettings />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'walkup',
        element: (
            <StaffLayout>
                <Suspended>
                    <WalkUpPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'receiving', // Route for staff to receive inventory
        element: (
            <StaffLayout>
                <Suspended>
                    <ReceivingPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'transfers',
        element: (
            <StaffLayout>
                <Suspended>
                    <TransfersPage />
                </Suspended>
            </StaffLayout>
        ),
    },
];


/** Accessible to both Admin and SiteAdmin */
const adminSharedRoutes = [
    {
        path: 'reports',
        element: (
            <StaffLayout>
                <Suspended>
                    <ReportsPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'products',
        element: (
            <StaffLayout>
                <Suspended>
                    <ProductsPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'products/:productId',
        element: (
            <StaffLayout>
                <Suspended>
                    <VariationPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'components',
        element: (
            <StaffLayout>
                <Suspended>
                    <ComponentsPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'components/:componentId/archive',
        element: (
            <StaffLayout>
                <Suspended>
                    <ComponentArchive />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'componenttypes',
        element: (
            <StaffLayout>
                <Suspended>
                    <ComponentTypesPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'componenttypes/:componentTypeId/archive',
        element: (
            <StaffLayout>
                <Suspended>
                    <ComponentTypeArchive />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'suppliers',
        element: (
            <StaffLayout>
                <Suspended>
                    <SuppliersPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'popular',
        element: (
            <StaffLayout>
                <Suspended>
                    <PopularProductsPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'inventory/:componentId',
        element: (
            <StaffLayout>
                <Suspended>
                    <InventoryUsagePage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'purchase-orders', // Route for admins to manage purchase orders
        element: (
            <StaffLayout>
                <Suspended>
                    <PurchaseOrdersPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'transfers',
        element: (
            <StaffLayout>
                <Suspended>
                    <AdminTransfersPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'eod-reports',
        element: (
            <StaffLayout>
                <Suspended>
                    <EodReportsPage />
                </Suspended>
            </StaffLayout>
        ),
    },

]

/** Accessible to global Admin only */
const adminOnlyRoutes = [
    {
        path: 'sites',
        element: (
            <StaffLayout>
                <Suspended>
                    <SitesPage />
                </Suspended>
            </StaffLayout>
        ),
    },
    {
        path: 'accounts',
        element: (
            <StaffLayout>
                <Suspended>
                    <AccountsPage />
                </Suspended>
            </StaffLayout>
        ),
    },
]

const AllRoutes = () => {
    return (
        <BrowserRouter>
            <Routes>
                {
                    publicRoutes.map((route) => {
                        return <Route key={route.path} path={route.path} element={route.element} />
                    })
                }

                {/* Customer-only routes */}
                <Route element={<RoleGuard allowedRoles={[Role.Customer]} />}>
                    {
                        customerRoutes.map(route => <Route key={route.path} path={route.path} element={route.element} />)
                    }
                </Route>

                {/* All staff roles: Admin, SiteAdmin, General */}
                <Route path="/staff" element={<RoleGuard allowedRoles={[Role.Admin, Role.SiteAdmin, Role.General]} />}>
                    {
                        staffRoutes.map(route => <Route key={route.path} path={route.path} element={route.element} />)
                    }
                </Route>

                {/* General Staff only routes */}
                <Route path="/staff" element={<RoleGuard allowedRoles={[Role.General]} />}>
                    <Route
                        path="inventory"
                        element={
                            <StaffLayout>
                <Suspended>
                    <StaffInventoryPage />
                </Suspended>
            </StaffLayout>
                        }
                    />
                    <Route
                        path="eod"
                        element={
                            <StaffLayout>
                <Suspended>
                    <EodPage />
                </Suspended>
            </StaffLayout>
                        }
                    />
                </Route>

                {/* Admin and SiteAdmin only routes */}
                <Route path="/staff" element={<RoleGuard allowedRoles={[Role.Admin, Role.SiteAdmin]} />}>
                    <Route
                        path="customers"
                        element={
                            <StaffLayout>
                <Suspended>
                    <CustomerManagementPage />
                </Suspended>
            </StaffLayout>
                        }
                    />
                </Route>

                {/* Admin + SiteAdmin + General shared routes */}
                <Route path="/admin" element={<RoleGuard allowedRoles={[Role.Admin, Role.SiteAdmin, Role.General]} />}>
                    {
                        adminSharedRoutes.map(route => <Route key={route.path} path={route.path} element={route.element} />)
                    }
                </Route>

                {/* Global Admin only routes */}
                <Route path="/admin" element={<RoleGuard allowedRoles={[Role.Admin]} />}>
                    {
                        adminOnlyRoutes.map(route => <Route key={route.path} path={route.path} element={route.element} />)
                    }
                </Route>
                </Routes>
        </BrowserRouter>
    )
}
export default AllRoutes